import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Card,
  Form,
  Switch,
  Button,
  Row,
  Col,
  Tag,
  Flex,
  Typography,
  Modal,
  message,
  Alert,
  Space,
  Select,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  SectionTitle,
  ProjectNameField,
  WebsiteBindingField,
} from "../../componets/common/ProjectFormComponents";
import TaskProgress from "../../componets/common/TaskProgress";
import DeleteConfirmModal from "../../componets/modals/Home/DeleteConfirm";
import TransposeConfirmModal from "../../componets/modals/Home/TransposeConfirm";
import { useGlobal } from "../../contexts/GlobalContext";
import request, { LONG_TASK_TIMEOUT } from "../../utils/request";
import { useCurrentProject } from "../../hooks/useCurrentProject";

const { Text } = Typography;

const DELETE_CONFIRM_TEXT = "我确定删除此项目及数据库且知道无法恢复";
const TRANSPOSE_CONFIRM_TEXT = "我知晓更改为时长计费后无法再改回Token付费";

// 支持语言（与后端 i18n 集合一致）
const LANG_OPTIONS = [
  { value: "zh-CN", label: "简体中文" },
  { value: "zh-TW", label: "繁体中文" },
  { value: "en", label: "English" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
];

const ProjectSettings: React.FC = () => {
  const navigate = useNavigate();
  const { API_BASE_URL, refreshProjects } = useGlobal();
  const { project, isPostchat } = useCurrentProject();
  const [form] = Form.useForm();

  const [saving, setSaving] = useState(false);
  const [podcastEnabled, setPodcastEnabled] = useState(false);
  const [podcastLoading, setPodcastLoading] = useState(false);

  // 删除 / 转换
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [transposeVisible, setTransposeVisible] = useState(false);
  const [transposeText, setTransposeText] = useState("");
  const [transposing, setTransposing] = useState(false);

  // 重建向量库进度
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildProgress, setRebuildProgress] = useState<{
    status: string;
    total: number;
    processed: number;
    failed: number;
    error?: string;
  } | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    if (!project) return;
    form.setFieldsValue({
      projectName: project.name,
      websites: project.refer ? project.refer.split(",") : [],
      summaryUrlBinding: project.url_binding,
      isPublic: project.is_public,
      languageMode: project.language_mode || "follow",
      defaultLanguage: project.default_language || "zh-CN",
      targetLanguage: project.target_language || project.default_language || "zh-CN",
    });
    setPodcastEnabled(!!project.enable_podcast);
  }, [project, form]);

  const startPolling = useCallback(() => {
    if (!project) return;
    stopPolling();
    const poll = async () => {
      try {
        const res = await request.get(`${API_BASE_URL}/api/v3/projects/${project.id}/rebuild-vdb/progress`);
        const p = res.data?.data;
        if (!p) return;
        setRebuildProgress(p);
        if (p.status === "completed" || p.status === "failed") {
          stopPolling();
          setRebuilding(false);
          if (p.status === "completed") {
            message.success(`重建完成：成功 ${p.processed} 条${p.failed ? `，失败 ${p.failed} 条` : ""}`);
          } else {
            message.error(p.error || "重建失败");
          }
        }
      } catch {
        /* 静默重试 */
      }
    };
    poll();
    timerRef.current = window.setInterval(poll, 3000);
  }, [API_BASE_URL, project, stopPolling]);

  if (!project) return null; // ProjectGuard 已处理

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res = await request.put(`${API_BASE_URL}/api/v3/projects/${project.id}`, {
        name: values.projectName,
        is_public: values.isPublic,
        // multi_language 仅留作老 v2 API 兼容字段，由语言模式推导：follow=多语言 / target=单语言
        multi_language: (values.languageMode || "follow") === "follow",
        refer: values.websites?.join(",") || "",
        url_binding: values.summaryUrlBinding,
        // 项目级语言策略（全局生效）
        language_mode: values.languageMode || "follow",
        default_language: values.defaultLanguage || "zh-CN",
        target_language:
          values.languageMode === "target"
            ? values.targetLanguage || values.defaultLanguage || "zh-CN"
            : "",
      });
      if (res.data.code === 200) {
        message.success("项目已更新");
        await refreshProjects();
      } else {
        message.error(res.data.msg);
      }
    } catch (e: any) {
      if (e?.errorFields) return; // 表单校验失败
      message.error(e?.response?.data?.msg || "更新失败，请重试");
    } finally {
      setSaving(false);
    }
  };

  const handlePodcastToggle = async (checked: boolean) => {
    setPodcastLoading(true);
    try {
      const res = await request.patch(`${API_BASE_URL}/api/v3/projects/${project.id}/podcast`, {
        enable_podcast: checked,
      });
      if (res.data.code === 200) {
        setPodcastEnabled(checked);
        message.success(checked ? "AI播客已开启" : "AI播客已关闭");
        await refreshProjects();
      } else {
        message.error(res.data.msg || "操作失败");
      }
    } catch {
      message.error("操作失败，请稍后重试");
    } finally {
      setPodcastLoading(false);
    }
  };

  const handleRebuild = () => {
    Modal.confirm({
      title: "重建 AI 索引数据库",
      content: "将删除并重新构建本项目所有知识库的 AI 索引，重建期间可能影响查询。每个项目每月仅限一次。",
      okText: "确认重建",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: async () => {
        try {
          setRebuilding(true);
          const res = await request.post(
            `${API_BASE_URL}/api/v3/projects/${project.id}/rebuild-vdb`,
            undefined,
            { timeout: LONG_TASK_TIMEOUT }
          );
          if (res.data.code === 200) {
            message.success("重建已开始，可在下方查看进度");
            startPolling();
          } else {
            message.error(res.data.msg || "操作失败");
            setRebuilding(false);
          }
        } catch (e: any) {
          if (e?.code === "ECONNABORTED") {
            message.warning("重建仍在后台执行，下方将持续展示进度");
            startPolling();
          } else {
            message.error(e?.response?.data?.msg || "操作失败，请稍后重试");
            setRebuilding(false);
          }
        }
      },
    });
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await request.delete(`${API_BASE_URL}/api/v3/projects/${project.id}`);
      if (res.data.code === 200) {
        message.success("项目已删除");
        setDeleteVisible(false);
        await refreshProjects();
        navigate("/");
      } else {
        message.error(res.data.msg);
      }
    } catch {
      message.error("删除失败，请重试");
    }
  };

  const handleConfirmTranspose = async () => {
    setTransposing(true);
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/projects/${project.id}/transpose`);
      if (res.data.code === 200) {
        message.success("计费方式更改成功");
        setTransposeVisible(false);
        await refreshProjects();
      } else {
        message.error(res.data.msg);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "更改失败，请重试");
    } finally {
      setTransposing(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 880, margin: "0 auto" }}>
      <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>项目设置</Typography.Title>
        <Tag color={isPostchat ? "blue" : "green"}>{isPostchat ? "全功能" : "仅摘要"}</Tag>
        <Text type="secondary" copyable={{ text: project.key_value }} style={{ fontSize: 12 }}>
          KEY: {project.key_value}
        </Text>
      </Flex>

      {/* 基本设置 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <SectionTitle icon="ri-information-line" title="基本设置" color="#1890ff" />
          <ProjectNameField />

          <SectionTitle icon="ri-translate-2" title="语言策略" color="#13c2c2" tooltip="对整个项目全局生效：摘要、对话、热词、推荐、播客、Agent 回复的输出语言" />
          <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
            项目级语言对该项目所有 AI 产物全局生效。子 Key 可单独覆盖语言；请求显式传入的 lang 优先级最高。
          </Text>
          <Form.Item name="languageMode" label="语言模式" style={{ maxWidth: 360 }}>
            <Select
              options={[
                { value: "follow", label: "跟随用户（按访客上报语言输出，缺省回落默认语言）" },
                { value: "target", label: "指定目标（强制使用单一目标语言）" },
              ]}
            />
          </Form.Item>
          <Form.Item name="defaultLanguage" label="默认语言（缺省回落）" style={{ maxWidth: 240 }}>
            <Select options={LANG_OPTIONS} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.languageMode !== cur.languageMode}
          >
            {({ getFieldValue }) =>
              getFieldValue("languageMode") === "target" && (
                <Form.Item name="targetLanguage" label="目标语言" style={{ maxWidth: 240 }}>
                  <Select options={LANG_OPTIONS} />
                </Form.Item>
              )
            }
          </Form.Item>

          <SectionTitle icon="ri-shield-keyhole-line" title="访问控制" color="#722ed1" tooltip="设置项目的访问权限和限制" />
          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Text>允许公开请求</Text>
            <Form.Item noStyle name="isPublic" valuePropName="checked">
              <Switch checkedChildren="已开启" unCheckedChildren="已关闭" />
            </Form.Item>
          </Flex>
          <Text type="secondary" style={{ fontSize: 13 }}>开启后，网站任何访客可获取摘要，无需验证身份。</Text>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.isPublic !== cur.isPublic}
          >
            {({ getFieldValue }) =>
              getFieldValue("isPublic") && (
                <div style={{ marginTop: 16 }}>
                  <WebsiteBindingField
                    title="绑定的网站"
                    subtitle="(仅允许这些网站的请求访问 API)"
                    noSites="未添加任何网站将允许所有网站请求，可能导致 API 密钥被滥用"
                  />
                </div>
              )
            }
          </Form.Item>

          <Flex justify="space-between" align="center" style={{ margin: "16px 0 6px" }}>
            <Space>
              <Text>摘要 URL 绑定</Text>
              <Tag color="processing">推荐开启</Tag>
            </Space>
            <Form.Item noStyle name="summaryUrlBinding" valuePropName="checked">
              <Switch checkedChildren="已开启" unCheckedChildren="已关闭" />
            </Form.Item>
          </Flex>
          <Text type="secondary" style={{ fontSize: 13 }}>
            开启后摘要与链接地址关联，避免文章小幅变动导致重复计费与加载等待。
          </Text>

          {isPostchat && (
            <>
              <Flex justify="space-between" align="center" style={{ margin: "16px 0 6px" }}>
                <Text>AI 播客</Text>
                <Switch
                  checked={podcastEnabled}
                  loading={podcastLoading}
                  onChange={handlePodcastToggle}
                  checkedChildren="已开启"
                  unCheckedChildren="已关闭"
                />
              </Flex>
              <Text type="secondary" style={{ fontSize: 13 }}>
                开启后可将知识库摘要转换为语音播客，需配置豆包 AI 密钥后使用。
              </Text>
            </>
          )}

          <div style={{ marginTop: 24 }}>
            <Button type="primary" loading={saving} onClick={handleSave}>保存更改</Button>
          </div>
        </Form>
      </Card>

      {/* 高级 / 危险操作 */}
      <Card title={<span style={{ color: "#d4380d" }}><i className="ri-tools-line" style={{ marginRight: 8 }} />高级操作</span>}>
        <Row gutter={[16, 16]}>
          {isPostchat && (
            <Col xs={24} md={12}>
              <Alert
                type="success"
                showIcon
                message="重建 AI 索引数据库"
                description={
                  <Flex vertical gap={8}>
                    <span>删除并重建本项目所有知识库的 AI 索引，适用于数据不一致或需要完全刷新时（每月限一次）。</span>
                    <Button type="primary" size="small" loading={rebuilding} onClick={handleRebuild} style={{ alignSelf: "flex-start" }}>
                      重建 AI 索引
                    </Button>
                    {rebuildProgress && rebuildProgress.status !== "idle" && (
                      <TaskProgress
                        title="向量库重建进度"
                        total={rebuildProgress.total}
                        current={rebuildProgress.processed + rebuildProgress.failed}
                        status={
                          rebuildProgress.status === "failed" ? "error"
                          : rebuildProgress.status === "completed" ? "completed"
                          : "running"
                        }
                        description={rebuildProgress.error || (rebuildProgress.failed > 0 ? `${rebuildProgress.failed} 条失败` : undefined)}
                      />
                    )}
                  </Flex>
                }
              />
            </Col>
          )}

          {!isPostchat && (
            <Col xs={24} md={12}>
              <Alert
                type="info"
                showIcon
                message="更改计费方式"
                description={
                  <Flex vertical gap={8}>
                    <span>将项目从 Token 计费改为时长计费，解锁智能对话/AI 搜索/热词等全功能（需 PostChat 会员，且不可改回）。</span>
                    <Button type="primary" size="small" onClick={() => { setTransposeText(""); setTransposeVisible(true); }} style={{ alignSelf: "flex-start" }}>
                      转为时长计费
                    </Button>
                  </Flex>
                }
              />
            </Col>
          )}

          <Col xs={24} md={12}>
            <Alert
              type="error"
              showIcon
              message="删除项目"
              description={
                <Flex vertical gap={8}>
                  <span>永久移除项目及其知识库、聊天历史与 API 配置，相关密钥立即失效，无法恢复。</span>
                  <Button danger size="small" onClick={() => { setDeleteText(""); setDeleteVisible(true); }} style={{ alignSelf: "flex-start" }}>
                    删除此项目
                  </Button>
                </Flex>
              }
            />
          </Col>
        </Row>
      </Card>

      <DeleteConfirmModal
        visible={deleteVisible}
        onCancel={() => setDeleteVisible(false)}
        onOk={handleConfirmDelete}
        confirmText={deleteText}
        setConfirmText={setDeleteText}
        requiredConfirmText={DELETE_CONFIRM_TEXT}
      />
      <TransposeConfirmModal
        visible={transposeVisible}
        onCancel={() => setTransposeVisible(false)}
        onOk={handleConfirmTranspose}
        confirmText={transposeText}
        setConfirmText={setTransposeText}
        requiredConfirmText={TRANSPOSE_CONFIRM_TEXT}
        transposing={transposing}
      />
    </div>
  );
};

export default ProjectSettings;
